import { Category, CategoryTypes } from "modules/categories";
import { Option } from "ui";

/**
 * `GET category-admin/all` returns a TREE of top-level categories, so a picker
 * fed with it directly can only ever offer main categories. Flatten it first.
 */
export const flattenCategoryTree = (categories: Category[]): Category[] => {
  const flat: Category[] = [];

  const walk = (nodes: Category[]) => {
    nodes.forEach((node) => {
      flat.push(node);
      if (node.childCategories?.length) {
        walk(node.childCategories);
      }
    });
  };

  walk(categories);

  return flat;
};

/**
 * Only leaves can carry an affinity rule that ever fires: products hold a
 * sub-sub-category id, and the resolver looks a rule up by that id with no walk
 * up the tree — so a rule written against a parent silently never matches.
 */
export const leafCategories = (categories: Category[]): Category[] =>
  flattenCategoryTree(categories).filter(
    (category) => category.type === CategoryTypes.SUB_CHILD_CATEGORY
  );

/**
 * Build "Parent › Name" labels for category pickers so same-named categories on
 * different tree levels stay distinguishable. Sorted by the display label.
 *
 * `nameSource` resolves the parent names, and defaults to the same list. Pass
 * the whole flattened tree when `categories` is a filtered slice of it —
 * otherwise the parents are missing and every label loses its prefix.
 */
export const buildCategoryOptions = (
  categories: Category[],
  nameSource: Category[] = categories
): Option[] => {
  const nameById = new Map(
    nameSource.map((category) => [category.id, category.name])
  );

  return categories
    .map((category) => {
      const parentName = category.parentCategoryId
        ? nameById.get(category.parentCategoryId)
        : undefined;

      return {
        value: category.id,
        label: parentName ? `${parentName} › ${category.name}` : category.name
      };
    })
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));
};

export const categoryNameById = (
  categories: Category[],
  id: string
): string | undefined => categories.find((c) => c.id === id)?.name;
